import { Helmet } from "react-helmet-async";

const SITE = {
    name: "DeCloudX",
    url: "https://decloudx.com",
    twitter: "@DeCloudX_com",
    defaultImage: "https://decloudx.com/og-image.png",
    defaultDescription:
        "DeCloudX is a patent-filed hybrid-decentralized cloud protocol combining decentralized compute, AI workloads, encrypted storage, and a Layer-1 EVM settlement chain into one resilient infrastructure layer for Internet 3.0.",
    defaultKeywords:
        "DeCloudX, decentralized cloud, DePIN, hybrid cloud, Layer-1 EVM, blockchain cloud, decentralized compute, AI compute, GPU DePIN, encrypted storage, on-chain settlement, $DCX, Web3 infrastructure, Internet 3.0",
};

/**
 * Reusable SEO component — handles <title>, meta, OG, Twitter, JSON-LD
 * Usage: <Seo title="..." description="..." path="/blog" article={{...}} />
 */
export default function Seo({
    title,
    description = SITE.defaultDescription,
    keywords = SITE.defaultKeywords,
    image = SITE.defaultImage,
    path = "",
    type = "website",
    article,
    noindex = false,
    jsonLd,
}) {
    const fullTitle = title
        ? `${title} · DeCloudX`
        : "DeCloudX — Cloud infrastructure for Internet 3.0";
    const canonical = `${SITE.url}${path || ""}`;

    return (
        <Helmet prioritizeSeoTags>
            <title>{fullTitle}</title>
            <meta name="description" content={description} />
            <meta name="keywords" content={keywords} />
            {noindex && <meta name="robots" content="noindex, nofollow" />}
            <link rel="canonical" href={canonical} />

            {/* Open Graph */}
            <meta property="og:type" content={type} />
            <meta property="og:site_name" content={SITE.name} />
            <meta property="og:url" content={canonical} />
            <meta property="og:title" content={fullTitle} />
            <meta property="og:description" content={description} />
            <meta property="og:image" content={image} />
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="630" />

            {/* Twitter */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:site" content={SITE.twitter} />
            <meta name="twitter:creator" content={SITE.twitter} />
            <meta name="twitter:title" content={fullTitle} />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:image" content={image} />

            {/* Article tags */}
            {article && (
                <>
                    <meta property="article:published_time" content={article.publishedAt} />
                    {article.updatedAt && <meta property="article:modified_time" content={article.updatedAt} />}
                    <meta property="article:author" content={article.author || "DeCloudX Team"} />
                    {article.tags?.map((t) => (
                        <meta property="article:tag" content={t} key={t} />
                    ))}
                    <meta property="article:section" content={article.category || "Infrastructure"} />
                </>
            )}

            {/* JSON-LD */}
            {jsonLd && (
                <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
            )}
        </Helmet>
    );
}

export { SITE };
